import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, uploadFile } from "../lib/api";
import { useCollection, PageHeader, CrudActions, Modal, Empty } from "../components/Crud";
import { DateTimePicker } from "../components/DatePicker";
import {
    Instagram, Facebook, Linkedin, Twitter, Calendar as CalIcon,
    Copy, Download, CheckCircle2, Plus, ImageIcon, FileVideo, Megaphone,
    Hash, SkipForward, ListIcon, Clock, Sparkles,
} from "lucide-react";

const PLATFORMS = [
    { key: "instagram", label: "Instagram", Icon: Instagram, color: "#E1306C" },
    { key: "facebook",  label: "Facebook",  Icon: Facebook,  color: "#1877F2" },
    { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin,  color: "#0A66C2" },
    { key: "x",         label: "X",         Icon: Twitter,   color: "#0F1419" },
];

// Industry-typical engagement windows (Sprout/Hootsuite/Buffer-style heuristics).
// days: 1=Mon … 5=Fri, 6=Sat, 0=Sun. Windows are local-time hour ranges, inclusive.
const BEST_TIMES = {
    instagram: { days: [2, 3, 4],       windows: [[11, 13], [19, 21]], hint: "Tue–Thu · 11am–1pm or 7–9pm" },
    facebook:  { days: [2, 3, 4],       windows: [[9, 11], [13, 15]],  hint: "Tue–Thu · 9–11am or 1–3pm" },
    linkedin:  { days: [2, 3, 4],       windows: [[8, 10], [12, 14]],  hint: "Tue–Thu · 8–10am or noon–2pm" },
    x:         { days: [1, 2, 3, 4, 5], windows: [[8, 10], [17, 19]],  hint: "Weekdays · 8–10am or 5–7pm" },
};

function hitsSweetSpot(platformKey, dt) {
    const cfg = BEST_TIMES[platformKey];
    if (!cfg || !dt || isNaN(dt)) return false;
    const day = dt.getDay();
    const hour = dt.getHours();
    if (!cfg.days.includes(day)) return false;
    return cfg.windows.some(([s, e]) => hour >= s && hour < e);
}

const STATUSES = [
    { key: "draft",     label: "Draft",     className: "bg-[var(--brand-purple)]/15 text-[var(--brand-purple)]" },
    { key: "scheduled", label: "Scheduled", className: "bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]" },
    { key: "posted",    label: "Posted",    className: "bg-[var(--status-green)]/15 text-[var(--status-green)]" },
    { key: "skipped",   label: "Skipped",   className: "bg-[rgba(26,19,24,0.12)] text-[var(--text-ink)]/65" },
];

const blankForm = () => ({
    caption: "", hashtags: "", platforms: [], campaign_name: "",
    scheduled_at: "", asset_url: "", asset_type: "", asset_name: "",
    notes_per_platform: {}, status: "draft",
});

// Local YYYY-MM-DD helper (Calendar.jsx uses the same trick to avoid UTC drift)
const ymd = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default function SocialScheduler() {
    const { t } = useTranslation();
    const { items, create, update, remove } = useCollection("social_posts");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blankForm());
    const [statusFilter, setStatusFilter] = useState("all");
    const [platformFilter, setPlatformFilter] = useState("all");
    const [q, setQ] = useState("");
    const [view, setView] = useState("list");
    const [cursor, setCursor] = useState(new Date());
    const [toast, setToast] = useState("");

    const togglePlatform = (k) =>
        setForm(f => ({ ...f, platforms: f.platforms.includes(k) ? f.platforms.filter(p => p !== k) : [...f.platforms, k] }));

    const setNoteFor = (k, v) =>
        setForm(f => ({ ...f, notes_per_platform: { ...(f.notes_per_platform || {}), [k]: v } }));

    const onAsset = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        const isVideo = file.type.startsWith("video/");
        const fd = new FormData(); fd.append("file", file);
        try {
            // uploadFile uses the shared 2-hour upload timeout so long social
            // assets (large videos on mobile) never hit the 30 s ceiling.
            const data = await uploadFile("/upload", fd);
            setForm(s => ({ ...s, asset_url: data.url, asset_type: isVideo ? "video" : "image", asset_name: file.name }));
        } catch {
            // Storage upload failed — fall back to inline base64 preview so
            // the draft still gets *something* attached until the user retries.
            const reader = new FileReader();
            reader.onload = () => setForm(s => ({ ...s, asset_url: reader.result, asset_type: isVideo ? "video" : "image", asset_name: file.name }));
            reader.readAsDataURL(file);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        // Implicit status nudge: blank schedule + draft => stays draft; date set + draft => move to scheduled.
        const status = form.status === "draft" && form.scheduled_at ? "scheduled" : form.status;
        const payload = { ...form, status };
        if (form.id) await update(form.id, payload);
        else await create(payload);
        setOpen(false); setForm(blankForm());
    };

    const captionWithTags = (p) => {
        const tags = (p.hashtags || "").trim();
        return tags ? `${p.caption}\n\n${tags}` : p.caption || "";
    };

    const copyCaption = async (p) => {
        try {
            await navigator.clipboard?.writeText(captionWithTags(p));
            setToast(`Caption copied for ${p.campaign_name || "post"}`);
            setTimeout(() => setToast(""), 1800);
        } catch {
            setToast("Copy failed — your browser blocked clipboard access.");
            setTimeout(() => setToast(""), 2400);
        }
    };

    const markPosted = (p) => update(p.id, { status: "posted", posted_at: new Date().toISOString() });
    const markSkipped = (p) => update(p.id, { status: "skipped" });

    const filtered = useMemo(() => items.filter(p => {
        if (statusFilter !== "all" && p.status !== statusFilter) return false;
        if (platformFilter !== "all" && !(p.platforms || []).includes(platformFilter)) return false;
        if (q) {
            const hay = `${p.caption || ""} ${p.hashtags || ""} ${p.campaign_name || ""}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
    }), [items, statusFilter, platformFilter, q]);

    return (
        <div data-testid="social-page">
            <PageHeader
                title={t("pages.socialTitle")}
                subtitle={t("pages.socialEyebrow")}
                action={
                    <button onClick={() => { setForm(blankForm()); setOpen(true); }}
                            data-testid="social-compose-btn"
                            className="btn-primary inline-flex items-center gap-2">
                        <Plus size={16}/> {t("pages.socialNewPost")}
                    </button>
                } />

            <div className="card-warm p-4 mb-5 flex items-center gap-3" data-testid="social-info-banner">
                <div className="w-9 h-9 rounded-full brand-gradient text-white grid place-items-center shrink-0">
                    <Sparkles size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight">{t("pages.socialTitle")} &mdash; {t("common.comingSoon").toLowerCase()}</div>
                    <div className="text-xs text-[var(--text-ink)]/65 mt-0.5">We&apos;re polishing this feature. It will be available in a future update.</div>
                </div>
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[var(--brand-purple)]/12 text-[var(--brand-purple)] font-semibold shrink-0">{t("common.comingSoon")}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${t("common.search")}…`}
                       data-testid="social-search"
                       className="flex-1 min-w-[14rem] max-w-sm px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="social-filter-status"
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    <option value="all">{t("pages.socialAllStatuses")}</option>
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}
                        data-testid="social-filter-platform"
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    <option value="all">{t("pages.socialAllPlatforms")}</option>
                    {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <div className="flex rounded-full bg-[rgba(26,19,24,0.06)] p-0.5">
                    {[
                        ["list", "List", ListIcon],
                        ["calendar", "Calendar", CalIcon],
                    ].map(([k, label, Icon]) => (
                        <button key={k} onClick={() => setView(k)} data-testid={`social-view-${k}`}
                                className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition ${view === k ? "brand-gradient text-white" : "text-[var(--text-ink)]/65 hover:text-[var(--text-ink)]"}`}>
                            <Icon size={12}/> {label}
                        </button>
                    ))}
                </div>
            </div>

            {view === "calendar" ? (
                <CalendarView posts={filtered} cursor={cursor} setCursor={setCursor}
                              onSelect={(p) => { setForm({ ...blankForm(), ...p }); setOpen(true); }} />
            ) : filtered.length === 0 ? (
                <Empty icon={Megaphone} title="No posts yet" hint="Draft a post, pick the platforms, schedule a time — your social plan, your way." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="social-grid">
                    {filtered.map(p => (
                        <PostCard key={p.id} p={p}
                                  onEdit={() => { setForm({ ...blankForm(), ...p }); setOpen(true); }}
                                  onDelete={() => window.confirm("Delete this post?") && remove(p.id)}
                                  onCopy={() => copyCaption(p)}
                                  onMarkPosted={() => markPosted(p)}
                                  onSkip={() => markSkipped(p)} />
                    ))}
                </div>
            )}

            {toast && (
                <div data-testid="social-toast"
                     className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg brand-gradient text-white text-sm vu-safe-bottom" style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom) + 0.5rem)" }}>
                    {toast}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit post" : "Compose post"} size="2xl">
                <form onSubmit={submit} className="grid gap-3" data-testid="social-form">
                    <label className="text-sm">Campaign / theme
                        <input value={form.campaign_name}
                               onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
                               placeholder="e.g. Spring launch · Client testimonial #2"
                               data-testid="social-input-campaign"
                               className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    </label>

                    <label className="text-sm">Caption *
                        <textarea required rows={5}
                                  value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                                  placeholder="What's on your mind?"
                                  data-testid="social-input-caption"
                                  className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] font-mono text-sm leading-snug" />
                        <div className="text-[11px] text-[var(--text-ink)]/55 mt-1 tabular-nums">
                            {form.caption.length} chars · X cap 280 · IG cap 2200
                        </div>
                    </label>

                    <label className="text-sm">Hashtags
                        <div className="relative mt-1">
                            <Hash size={12} className="absolute left-3 top-3 text-[var(--text-ink)]/45" />
                            <input value={form.hashtags}
                                   onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
                                   placeholder="#virtualassistant #studentlife (space-separated)"
                                   data-testid="social-input-hashtags"
                                   className="w-full pl-7 pr-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                        </div>
                    </label>

                    <div>
                        <div className="text-sm mb-1">Platforms *</div>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map(p => {
                                const on = form.platforms.includes(p.key);
                                return (
                                    <button type="button" key={p.key} onClick={() => togglePlatform(p.key)}
                                            data-testid={`social-platform-${p.key}`}
                                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition ${on ? "border-transparent text-white shadow-sm" : "border-[rgba(26,19,24,0.15)] text-[var(--text-ink)]/70 hover:border-[rgba(26,19,24,0.35)]"}`}
                                            style={on ? { background: p.color } : {}}>
                                        <p.Icon size={14} /> {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <label className="text-sm">Asset (image or video)
                        <div className="flex items-center gap-3 mt-1">
                            <input type="file" accept="image/*,video/*" onChange={onAsset}
                                   data-testid="social-input-asset"
                                   className="text-sm" />
                            {form.asset_url && (form.asset_type === "video" ? (
                                <div className="inline-flex items-center gap-1 text-xs text-[var(--text-ink)]/65">
                                    <FileVideo size={14} /> {form.asset_name || "video.mp4"}
                                </div>
                            ) : (
                                <img src={form.asset_url} alt="" className="h-12 w-12 object-cover rounded-lg" />
                            ))}
                        </div>
                        <div className="text-[11px] text-[var(--text-ink)]/55 mt-1">Placeholder asset — used for preview and download only.</div>
                    </label>

                    <label className="text-sm">Schedule for
                        <div className="mt-1">
                            <DateTimePicker value={form.scheduled_at}
                                            onChange={(v) => setForm({ ...form, scheduled_at: v })}
                                            testid="social-input-schedule"
                                            placeholder="Date & time (leave blank for draft)" />
                        </div>
                        <div className="text-[11px] text-[var(--text-ink)]/55 mt-1">Leave blank to keep as a draft.</div>
                        <BestTimeHint platforms={form.platforms} scheduledAt={form.scheduled_at} />
                    </label>

                    <label className="text-sm">Status
                        <select value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                data-testid="social-input-status"
                                className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </label>

                    {form.platforms.length > 0 && (
                        <div>
                            <div className="text-sm mb-1">Platform-specific notes</div>
                            <div className="grid gap-2">
                                {form.platforms.map(k => {
                                    const def = PLATFORMS.find(p => p.key === k);
                                    return (
                                        <div key={k} className="grid grid-cols-[110px,1fr] items-start gap-2">
                                            <div className="inline-flex items-center gap-1.5 text-xs pt-2" style={{ color: def?.color }}>
                                                <def.Icon size={12}/> {def?.label}
                                            </div>
                                            <textarea rows={2} value={form.notes_per_platform?.[k] || ""}
                                                      onChange={(e) => setNoteFor(k, e.target.value)}
                                                      placeholder={`Tweaks for ${def?.label}… (e.g. trim copy, swap hashtags, vertical crop)`}
                                                      data-testid={`social-note-${k}`}
                                                      className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
                        <button type="submit" data-testid="social-save-btn" className="btn-primary">Save post</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

function PostCard({ p, onEdit, onDelete, onCopy, onMarkPosted, onSkip }) {
    const status = STATUSES.find(s => s.key === p.status) || STATUSES[0];
    const platforms = (p.platforms || []).map(k => PLATFORMS.find(pl => pl.key === k)).filter(Boolean);
    const scheduledLabel = p.scheduled_at ? p.scheduled_at.replace("T", " · ") : "";
    return (
        <div className="card-warm overflow-hidden flex flex-col" data-testid={`social-card-${p.id}`}>
            {p.asset_url && (
                p.asset_type === "video" ? (
                    <video src={p.asset_url} className="w-full h-44 object-cover bg-black" controls />
                ) : (
                    <img src={p.asset_url} alt="" className="w-full h-44 object-cover" />
                )
            )}
            <div className="p-4 flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    {platforms.map(pl => <pl.Icon key={pl.key} size={15} style={{ color: pl.color }} data-testid={`social-card-${p.id}-platform-${pl.key}`} />)}
                    {p.campaign_name && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[rgba(26,19,24,0.05)] text-[var(--text-ink)]/70">{p.campaign_name}</span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider ml-auto px-2 py-0.5 rounded-full ${status.className}`} data-testid={`social-card-${p.id}-status`}>{status.label}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap line-clamp-4" data-testid={`social-card-${p.id}-caption`}>{p.caption}</div>
                {p.hashtags && <div className="text-xs text-[var(--brand-purple)] line-clamp-2">{p.hashtags}</div>}
                {scheduledLabel && (
                    <div className="text-[11px] text-[var(--text-ink)]/55 inline-flex items-center gap-1"><CalIcon size={11}/> {scheduledLabel}</div>
                )}
                {p.notes_per_platform && Object.entries(p.notes_per_platform).filter(([, v]) => v).length > 0 && (
                    <details className="text-xs text-[var(--text-ink)]/70 mt-1" data-testid={`social-card-${p.id}-notes`}>
                        <summary className="cursor-pointer text-[var(--brand-orange)] hover:underline">Platform notes</summary>
                        <ul className="mt-1 pl-3 space-y-0.5">
                            {Object.entries(p.notes_per_platform).filter(([, v]) => v).map(([k, v]) => {
                                const def = PLATFORMS.find(pl => pl.key === k);
                                return <li key={k}><span style={{ color: def?.color }}>{def?.label}:</span> {v}</li>;
                            })}
                        </ul>
                    </details>
                )}
                <div className="flex items-center gap-1 pt-2 mt-auto border-t border-[rgba(26,19,24,0.06)]">
                    <button onClick={onCopy} title="Copy caption + hashtags"
                            data-testid={`social-copy-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full btn-ghost">
                        <Copy size={12}/> Copy caption
                    </button>
                    {p.asset_url && (
                        <a href={p.asset_url} download={p.asset_name || ""} target="_blank" rel="noreferrer"
                           title="Preview / download asset"
                           data-testid={`social-download-${p.id}`}
                           className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full btn-ghost">
                            <Download size={12}/> Asset
                        </a>
                    )}
                    {p.status !== "posted" && (
                        <button onClick={onMarkPosted} title="Mark as posted"
                                data-testid={`social-mark-posted-${p.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full brand-gradient text-white">
                            <CheckCircle2 size={12}/> Posted
                        </button>
                    )}
                    {p.status === "scheduled" && (
                        <button onClick={onSkip} title="Skip this slot"
                                data-testid={`social-skip-${p.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full btn-ghost">
                            <SkipForward size={12}/> Skip
                        </button>
                    )}
                    <div className="ml-auto">
                        <CrudActions idBase={`sp-${p.id}`} onEdit={onEdit} onDelete={onDelete} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function CalendarView({ posts, cursor, setCursor, onSelect }) {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startDay = (first.getDay() + 6) % 7;             // Monday-first
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= last; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

    const postsByDay = useMemo(() => {
        const map = {};
        for (const p of posts) {
            if (!p.scheduled_at) continue;
            const key = p.scheduled_at.slice(0, 10);
            (map[key] = map[key] || []).push(p);
        }
        return map;
    }, [posts]);

    const today = ymd(new Date());

    return (
        <div data-testid="social-calendar">
            <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="btn-ghost text-sm" data-testid="social-cal-prev">←</button>
                <div className="font-serif text-2xl px-2">{cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })}</div>
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="btn-ghost text-sm" data-testid="social-cal-next">→</button>
                <button onClick={() => setCursor(new Date())} className="btn-ghost text-xs ml-2" data-testid="social-cal-today">Today</button>
                <span className="ml-auto text-xs text-[var(--text-ink)]/55">Drafts without a date won&apos;t show on the calendar.</span>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55 mb-1">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d} className="px-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (!d) return <div key={`sc-empty-${i}`} className="aspect-[5/4] bg-transparent" />;
                    const key = ymd(d);
                    const dayPosts = postsByDay[key] || [];
                    const isToday = key === today;
                    return (
                        <div key={`sc-${key}`} data-testid={`social-cal-cell-${key}`}
                             className={`aspect-[5/4] rounded-lg p-1.5 border text-[11px] overflow-hidden flex flex-col gap-1 ${isToday ? "border-[var(--brand-orange)] bg-[rgba(255,107,53,0.06)]" : "border-[rgba(26,19,24,0.08)] bg-white/55"}`}>
                            <div className={`flex items-center justify-between ${isToday ? "text-[var(--brand-orange)] font-medium" : "text-[var(--text-ink)]/65"}`}>
                                <span>{d.getDate()}</span>
                                {dayPosts.length > 0 && <span className="text-[9px] px-1 rounded bg-[var(--brand-purple)]/12 text-[var(--brand-purple)]">{dayPosts.length}</span>}
                            </div>
                            <div className="flex flex-col gap-0.5 overflow-hidden">
                                {dayPosts.slice(0, 3).map(p => {
                                    const main = PLATFORMS.find(pl => pl.key === (p.platforms?.[0]));
                                    return (
                                        <button key={p.id} onClick={() => onSelect(p)}
                                                data-testid={`social-cal-post-${p.id}`}
                                                className="text-left truncate rounded px-1 py-0.5 hover:bg-[rgba(123,45,142,0.08)] inline-flex items-center gap-1">
                                            {main && <main.Icon size={9} style={{ color: main.color }} />}
                                            <span className="truncate">{p.campaign_name || p.caption?.slice(0, 18) || "Untitled"}</span>
                                        </button>
                                    );
                                })}
                                {dayPosts.length > 3 && (
                                    <div className="text-[9px] text-[var(--text-ink)]/55 px-1">+{dayPosts.length - 3} more</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function BestTimeHint({ platforms, scheduledAt }) {
    if (!platforms || platforms.length === 0) return null;
    const dt = scheduledAt ? new Date(scheduledAt) : null;
    const hasDate = dt && !isNaN(dt);
    const rows = platforms
        .map(k => ({ key: k, def: PLATFORMS.find(p => p.key === k), cfg: BEST_TIMES[k] }))
        .filter(r => r.def && r.cfg);

    if (rows.length === 0) return null;

    return (
        <div className="mt-2 rounded-xl border border-dashed border-[rgba(123,45,142,0.25)] bg-[rgba(255,107,53,0.04)] p-2.5"
             data-testid="best-time-hint">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[var(--brand-purple)] mb-1.5">
                <Sparkles size={11} /> Best time to post
                <span className="text-[10px] normal-case text-[var(--text-ink)]/50 tracking-normal">· typical engagement windows</span>
            </div>
            <ul className="space-y-1">
                {rows.map(({ key, def, cfg }) => {
                    const hit = hasDate && hitsSweetSpot(key, dt);
                    return (
                        <li key={key} className="flex items-center gap-2 text-xs" data-testid={`best-time-${key}`}>
                            <def.Icon size={11} style={{ color: def.color }} />
                            <span className="text-[var(--text-ink)]/75 min-w-[62px]">{def.label}</span>
                            <Clock size={10} className="text-[var(--text-ink)]/45" />
                            <span className="text-[var(--text-ink)]/65">{cfg.hint}</span>
                            {hasDate && (
                                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${hit
                                    ? "bg-[var(--status-green)]/15 text-[var(--status-green)]"
                                    : "bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]"}`}
                                      data-testid={`best-time-${key}-${hit ? "hit" : "miss"}`}>
                                    {hit ? "✓ in sweet spot" : "outside window"}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
